<template>
  <div
    role="group"
    :aria-label="$string('accessibility.cart.line_item_label')"
    class="line-item"
    :class="{
      [$class('loading')]: !lineItem,
      [$class('small')]: small,
      [$class('updating')]: state === 'updating',
      [$class('disabled')]: isShippingUnavailable(),
      'is-simple': simple,
      'sold-out': isSoldOut
    }"
  >
    <div class="line-item__grid">
      <component
        :is="isFreeGift ? 'div' : 'a'"
        class="line-item__image-container"
        :href="$localizeUrl(lineItem.url)"
      >
        <span
          class="visually-hidden"
          v-text="`${$string('accessibility.view')} ${lineItem.featured_image?.alt}`"
        />

        <responsive-image
          class="line-item__image"
          :image="lineItem.featured_image"
          :lazyload="false"
          :padding="false"
          ratio="1:1"
          sizes="(min-width: 1024px) 203px, 165px"
          widths="165, 203, 330, 406, 495"
        />

        <div
          v-if="isSoldOut"
          class="line-item__sold-out-overlay"
        >
          <p
            class="line-item__sold-out-message text-body-2xs-regular"
            v-text="$string('cart.line_item.sold_out')"
          />
        </div>
      </component>

      <div class="line-item__body">
        <p
          v-if="!isFreeGift && lineItem.properties?.metafields?.discountCode"
          class="line-item__discount-code mobile text-body-2xs-regular"
          v-text="$string('cart.line_item.discount_code', { replace: {
            discount: lineItem.properties?.metafields?.discountCode,
          } })"
        />

        <ul
          v-else-if="lineItem.properties?.metafields &&
            $variable('settings.cart.showBadgesOnCartLineItems') &&
            lineItem.properties?.metafields?.topBadge"
          class="line-item__badges"
        >
          <product-badge
            :product="lineItem.properties"
            badge-class="text-body-2xs-regular"
            badge-element="li"
            badge-type="topBadge"
          />
        </ul>

        <div
          class="line-item__details"
          :class="{ 'has-promo-pricing': discountMessage }"
        >
          <p
            v-if="isFreeGift"
            class="line-item__free-gift-message text-body-2xs-regular"
            v-text="$string('cart.line_item.free_gift')"
          />

          <h2>
            <component
              :is="isFreeGift ? 'p' : 'a'"
              class="line-item__title text-body-2xs-regular"
              :href="$localizeUrl(lineItem.url)"
              v-text="lineItem.product_title"
            />
          </h2>

          <div
            v-if="isShippingUnavailable()"
            class="product-form__shipping-unavailable"
            v-text="$string('product.form.shipping_unavailable')"
          />

          <p
            v-if="isFreeGift"
            class="line-item__free-gift-message text-body-2xs-regular"
            v-text="$string('cart.line_item.free')"
          />

          <p
            v-else
            class="line-item__price-container text-body-2xs-regular"
          >
            <product-prices
              class="line-item__price"
              class-price="line-item__final-price text-body-2xs-regular"
              class-compare-at-price="line-item__compare-price text-other-sale-s"
              :compare-at-price="compareAtPrice"
              element="span"
              :price="lineItem?.final_price"
              :final-sale="lineItem.tags?.includes('finalsale') ||
                lineItem.tags?.includes('Final Sale')"
              :price-first="false"
              location="cart"
            />
          </p>

          <p
            v-if="(!lineItem.product_has_only_default_variant || lineItem.gift_card) &&
              attributeText(sizelabel) &&
              !isGiftCard"
            class="line-item__variant text-body-2xs-regular"
            :class="sizelabel"
            v-text="attributeText(sizelabel)"
          />

          <template v-for="swatchOptionName of swatchOptionNames">
            <p
              v-if="(!lineItem.product_has_only_default_variant || lineItem.gift_card) &&
                attributeText(swatchOptionName) &&
                !isGiftCard"
              :key="swatchOptionName"
              class="line-item__variant text-body-2xs-regular"
              :class="swatchOptionName"
              v-text="attributeText(swatchOptionName)"
            />
          </template>

          <div
            v-if="isGiftCard"
            class="line-item__gift-card"
          >
            <div v-if="lineItem.properties['Recipient name']">
              <span
                class="text-body-2xs-regular"
                v-text="$string('cart.line_item.gift_card.name')"
              />
              <span
                v-if="lineItem.properties['Recipient name']"
                class="line-item__variant text-body-2xs-regular"
                v-text="lineItem.properties['Recipient name']"
              />
            </div>

            <div v-if="lineItem.properties['Recipient email']">
              <span
                class="text-body-2xs-regular"
                v-text="$string('cart.line_item.gift_card.email')"
              />

              <span
                class="line-item__variant text-body-2xs-regular"
                v-text="lineItem.properties['Recipient email']"
              />
            </div>

            <div v-if="lineItem.properties['Message']">
              <span
                class="text-body-2xs-regular"
                v-text="$string('cart.line_item.gift_card.message')"
              />

              <span
                class="line-item__variant text-body-2xs-regular"
                v-text="truncatedMessage"
              />
            </div>
          </div>

          <p
            v-if="!isFreeGift && lineItem.properties?._preOrderDate"
            class="line-item__line-item-properties text-body-2xs-regular"
            v-html="preorderMessage"
          />

          <p
            v-if="!isFreeGift && lineItem.properties?.metafields?.discountCode"
            class="line-item__discount-code desktop text-body-2xs-regular"
            v-text="$string('cart.line_item.discount_code', { replace: {
              discount: lineItem.properties?.metafields?.discountCode,
            } })"
          />
          <p
            v-if="discountMessage"
            class="line-item__discount-message desktop text-body-2xs-regular"
            v-text="discountMessage"
          />
        </div>

        <p
          v-if="!isFreeGift && !small && (lineItem.tags?.includes('finalsale') ||
            lineItem.tags?.includes('Final Sale'))"
          class="line-item__sale-warning mobile text-body-2xs-regular"
          v-text="$string('cart.line_item.sales_warning')"
        />

        <div
          v-if="!simple"
          class="line-item__actions desktop"
          :class="{
            'single-button': isFreeGift || lineItem.inventory_quantity === 0,
            'right-aligned': isSoldOut && small,
          }"
        >
          <quantity-selector
            v-if="!isGiftCard && !isFreeGift && !isSoldOut"
            :class="{ 'right-aligned': isSoldOut && small || isGiftCard }"
            class="line-item__quantity-selector"
            :line-item="lineItem"
            :quantity="quantity"
            :state="state"
            @update-quantity="handleQuantityUpdate"
          />

          <p
            v-if="!isFreeGift && !small"
            class="line-item__sale-warning text-body-2xs-regular"
            :class="{
              'hidden': !lineItem.tags?.includes('finalsale') &&
                !lineItem.tags?.includes('Final Sale')
            }"
            v-text="$string('cart.line_item.sales_warning')"
          />

          <div
            class="line-item__buttons-wrapper"
            :class="{ 'single-button': isFreeGift || isSoldOut || small }"
          >
            <button
              class="line-item__remove"
              :class="{ [$class('disabled')]: state === 'updating' }"
              type="button"
              @click="handleRemove"
            >
              <span
                class="text-body-2xs-underline"
                v-text="isShippingUnavailable() ?
                  $string('cart.line_item.remove_to_checkout') :
                  $string('cart.line_item.remove')"
              />
            </button>

            <wishlist-button
              v-if="renderWishlistBtn &&
                !isFreeGift && !isSoldOut && !small"
              class="line-item__add-wishlist"
              :product="getProductIdToWishlist()"
              template="line-item"
              :variant="getProductIdToWishlist()"
              :disabled="state === 'updating'"
              @click="handleRemove"
            />
          </div>
        </div>

        <div
          v-if="!simple"
          class="line-item__actions mobile"
        >
          <quantity-selector
            v-if="!isFreeGift && !isSoldOut && !isGiftCard"
            class="line-item__quantity-selector"
            :line-item="lineItem"
            :quantity="quantity"
            :state="state"
            @update-quantity="handleQuantityUpdate"
          />

          <wishlist-button
            v-if="renderWishlistBtn &&
              !isFreeGift && !isSoldOut && !small"
            class="line-item__add-wishlist"
            :product="getProductIdToWishlist()"
            template="line-item"
            :variant="getProductIdToWishlist()"
            :disabled="state === 'updating'"
            @click="handleRemove"
          />

          <button
            class="line-item__remove"
            :class="{ [$class('disabled')]: state === 'updating' }"
            type="button"
            @click="handleRemove"
          >
            <span
              class="text-body-2xs-underline"
              v-text="isShippingUnavailable() ?
                $string('cart.line_item.remove_to_checkout') :
                $string('cart.line_item.remove')"
            />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Line item (line-item)
 * -----------------------------------------------------------------------------
 * Displays line items in cart, cart drawer, and order.
 *
 * @param {Boolean|Object} lineItem - Line item in cart response or order.
 * @param {Boolean} [simple] - Show only image and text, no buttons.
 * @param {Boolean} [small] - Use mobile layout at all breakpoints.
 *
 */
import { mapActions, mapState } from 'vuex'

import ProductPrices from '~global/product-prices/product-prices'
import QuantitySelector from '~async/quantity-selector/quantity-selector'
import ResponsiveImage from '~global/images/responsive-image'
import ProductBadge from '~global/product-badge/product-badge'
import WishlistButton from '~async/wishlist-button/wishlist-button'

import { convertHandleToSentenceCase } from '~/helpers/convert'
import { queryFormatProduct } from '~/helpers/shopify'

export default {
  name: 'LineItem',

  components: {
    ProductPrices,
    QuantitySelector,
    ResponsiveImage,
    ProductBadge,
    WishlistButton,
  },

  props: {
    cartItems: {
      type: Array,
      required: true,
    },
    lineItem: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    simple: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      error: '',
      quantity: 1,
      state: 'loading',
      sizelabel: 'size',
      swatchOptionNames: window.cnvs?.settings?.product.productSwatchOptionNames || [],
      discountMessage: null,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cartStatus: (state) => state.cart.status,
      cartAttributes: (state) => state.cart.response.attributes,
    }),

    /**
     * Check if FGWP products exist
     */
    numFGWPLineItems() {
      const conditionCollection = this.$variable('settings.fgwp.conditionCollection')

      return this.cartItems?.reduce((total, item) => {
        const qualifies = item.collections?.some(
          // eslint-disable-next-line no-underscore-dangle
          (collection) => collection.id === conditionCollection && !item.properties?._is_fgwp,
        )

        return qualifies ? total + 1 : total
      }, 0)
    },

    /**
     * Compute compare at price.
     * - Uses compare at price if it exists.
     * - Otherwise if script discount has been applied use original price.
     * - This way it always shows the largest original price.
     * - Otherwise show no compare at price.
     * @returns {Number|Boolean}
     */
    compareAtPrice() {
      if (this.lineItem?.compare_at_price) {
        return this.lineItem?.compare_at_price
      }

      if (this.lineItem?.original_price !== this.lineItem?.final_price) {
        return this.lineItem?.original_price
      }

      return false
    },

    /**
     * Computes if this is a Gift card item.
     */
    isGiftCard() {
      if (!this.lineItem?.product_type) {
        return
      }

      return this.lineItem?.product_type?.toLowerCase() === 'gift card'
    },

    preorderMessage() {
      const date = this.$formatPreorderDate({
        // eslint-disable-next-line no-underscore-dangle
        date: this.lineItem.properties?._preOrderDate,
        dateFormat: 'preorder_cart_html',
      })

      return this.$string('product.preorder.cart', {
        replace: { date },
      })
    },

    /**
     * Truncates gift message
     */
    truncatedMessage() {
      const message = this.lineItem.properties.Message
      const maxLength = 100
      return message.length > maxLength
        ? `${message.substring(0, maxLength)} .....`
        : message
    },

    /**
     * Compute if line item is limited by inventory quantity.
     * @returns {Boolean}
     */
    limitedInventory() {
      return (
        this.lineItem.inventory_management &&
        this.lineItem.inventory_policy === 'deny'
      )
    },

    /**
     * Compute line item properties based on theme setting.
     * - Only display those in setting in the order they're added.
     * - Won't display line item if value is blank or false.
     * @returns {String}
     */
    lineItemProperties() {
      const propertiesSetting = this.$variable('settings.lineItemProperties')
        ? this.$variable('settings.lineItemProperties').trim()
        : ''

      if (
        !propertiesSetting ||
        !this.lineItem.properties ||
        !Object.keys(this.lineItem.properties).length
      ) {
        return ''
      }

      const properties = propertiesSetting
        .split('\n')
        .map((property) => {
          const formattedKey = convertHandleToSentenceCase(property)
          const key = `${formattedKey.slice(0, 1).toUpperCase()}${formattedKey.slice(1)}`
          const value = this.lineItem.properties[property.trim()]

          if (!value || (typeof value === 'string' && !value.trim().length)) {
            return false
          }

          return typeof value === 'boolean'
            ? key
            : `${key}: ${value}`
        })
        .filter(Boolean)

      return properties.join('<br>')
    },

    /**
     * Compute notification state.
     * @returns {Object}
     */
    notification() {
      if (!this.lineItem || (!this.limitedInventory && !this.error)) {
        return { display: false }
      }

      const notification = {}

      /**
       * Display error message from cart response.
       */
      if (this.error) {
        notification.display = true
        notification.text = this.error
        notification.title = ''
        return notification
      }

      /**
       * Display manual error notifications.
       */
      if (this.lineItem.quantity > this.lineItem.inventory_quantity) {
        notification.display = true
        notification.title = this.$string('cart.notifications.limited_stock.title')

        notification.text = this.$string(
          'cart.notifications.limited_stock.text',
          {
            pluralise: this.lineItem.inventory_quantity,
            replace: { count: this.lineItem.inventory_quantity },
          },
        )

      } else if (this.lineItem.quantity === this.lineItem.inventory_quantity) {
        notification.display = true
        notification.title = this.$string('cart.notifications.maximum_quantity.title')

        notification.text = this.$string(
          'cart.notifications.maximum_quantity.text',
          {
            pluralise: this.lineItem.inventory_quantity,
            replace: { count: this.lineItem.inventory_quantity },
          },
        )
      }

      return notification
    },

    /**
     * Compute variant text.
     * @returns {String}
     */
    variantText() {
      if (this.lineItem.gift_card) {
        return this.$formatMoney(this.lineItem.original_price, cnvs?.store.moneyFormat, true)
      }

      if (!this.lineItem.options_with_values) {
        return this.lineItem.variant_title
      }

      return this.lineItem.options_with_values?.map((option) => {
        return this.$string(
          'cart.line_item.variant',
          {
            replace: {
              option_name: option.name,
              selected_value: option.value,
            },
          },
        )
      }).join(' / ')
    },

    /**
     * Is line item free gift
     */
    isFreeGift() {
      // eslint-disable-next-line no-underscore-dangle
      return this.lineItem.properties?._is_fgwp
    },

    isSoldOut() {
      return this.lineItem.inventory_quantity === 0
    },

    /**
     * Renders wishlist button in all cases
     */
    renderWishlistBtn() {
      return true
    },
  },

  watch: {

    /**
     * Watch cart status items to see if item is being updated.
     */
    'cartStatus.items'() {
      if (this.cartStatus.items.includes(this.lineItem.key)) {
        this.state = 'updating'
        return
      }

      this.state = 'ready'
    },

    /**
     * Watch item quantity to update quantity if updated elsewhere.
     */
    'lineItem.quantity'() {
      this.quantity = this.lineItem.quantity
    },
  },

  mounted() {
    this.state = 'ready'
    this.quantity = this.lineItem.quantity
    this.sizelabel = this.$string('cart.line_item.size_opt')
    if (cnvs?.settings?.product?.promoMsgToggle) {
      this.fetchDiscountMessage(this.lineItem)
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      updateItem: 'cart/updateItem',
      removeItem: 'cart/updateItem',
      updateCartAttributes: 'cart/updateCartAttributes',
    }),

    /**
     * Handle quantity update event.
     * @param {String} quantity - New quantity.
     */
    async handleQuantityUpdate(quantity) {
      this.quantity = quantity

      try {
        this.error = ''
        this.state = 'updating'

        await this.updateItem([
          {
            id: this.lineItem.key,
            quantity: this.quantity,
          },
        ])

        this.state = 'ready'

      } catch (error) {
        this.state = 'error'
        this.quantity = this.lineItem.quantity

        if (error.description) {
          this.handleErrorMessage(error.description)
          return
        }

        cnvs.ReportError('Failed to update line item quantity', error)
      }
    },

    /**
     * Handle remove click.
     */
    async handleRemove() {
      if (this.state === 'updating') {
        return
      }

      // If removing free gift while the cart still qualifies,
      // cancel the gift
      if (this.isFreeGift && this.numFGWPLineItems > 0) {
        await this.addCartAttribute()
        localStorage.setItem('fgwp_canceled', 'true')

      // If removing free gift while the cart does not qualify,
      // undo cancellation
      } else if (this.isFreeGift && this.numFGWPLineItems === 0) {
        await this.removeCartAttribute()
        localStorage.removeItem('fgwp_canceled')

      // If removing the last fgwp qualifying item, undo cancellation
      } else if (this.lineItem.collections?.find(
        ({ id: cId }) => cId === this.$variable('settings.fgwp.conditionCollection'),
      ) && this.numFGWPLineItems === 1) {
        await this.removeCartAttribute()
        localStorage.removeItem('fgwp_canceled')
      }

      await this.removeItem(this.lineItem.key)
    },

    /**
     * Add cart attribute once free gift is removed
     */
    async addCartAttribute() {
      try {
        await this.updateCartAttributes({
          attributes: {
            ...this.cartAttributes,
            fgwp_canceled: true,
          },
        })
      } catch (error) {
        // do nothing
      }
    },

    /**
     * Remove cart attribute once free gift parent is removed
     */
    async removeCartAttribute() {
      try {
        await this.updateCartAttributes({
          attributes: {
            ...this.cartAttributes,
            fgwp_canceled: false,
          },
        })
      } catch (error) {
        // do nothing
      }
    },

    /**
     * Handle error message.
     * @param {Object} errorDescription - Error description from add request.
     */
    handleErrorMessage(errorDescription) {
      if (typeof errorDescription === 'string') {
        this.error = errorDescription
        return
      }

      this.error = Object.values(errorDescription)
        .flat()
        .map((error) => {
          return error.endsWith('.') ? error : `${error}.`
        })
        .join('<br>')
    },

    /**
     * Get size and color attribute values of the line item
     * @param {Object} attribute - Line item attribute object
     */
    attributeText(attribute) {
      const lowStock = this.lineItem.inventory_quantity < this.$variable('settings.lowStockThreshold') &&
        this.lineItem.inventory_quantity > 0

      const option = this.lineItem.options_with_values?.find(
        (opt) => opt.name.toLowerCase() === attribute,
      )

      if (!option) {
        return null
      }

      if (option.name.toLowerCase() === 'size' || option.name.toLowerCase() === 'taille') {
        return lowStock
          ? this.$string(`cart.line_item.${option.name.toLowerCase()}_low_stock`, {
            replace: {
              value: option.value,
            },
          })
          : this.$string(`cart.line_item.${option.name.toLowerCase()}`, {
            replace: {
              value: option.value,
            },
          })
      }

      if (this.$variable('settings.product.productSwatchOptionNames')?.includes(option.name.toLowerCase())) {
        return this.$string(`cart.line_item.${option.name?.toLowerCase()}`, {
          replace: {
            value: option.value,
          },
        })
      }

      return null
    },

    /**
     * Adopt product object for wishlist
     */
    getProductIdToWishlist() {
      return {
        ...this.lineItem,
        id: this.lineItem.product_id,
        selected_or_first_available_variant: {
          compare_at_price: this.lineItem.compare_at_price,
          price: this.lineItem.price,
        },
      }
    },

    /**
     * Checks if product ships to another country
     */
    isShippingUnavailable() {
      if (cnvs?.settings?.blockedProduct &&
        this.lineItem.tags?.includes('international: block') &&
        Shopify.country !== 'US') {
        return true
      }

      return false
    },

    /**
     * Renders discount promo message
     * @returns {String}
     */
    async fetchDiscountMessage(line) {
      const productData = await this.queryProduct(line.handle)
      const messageObj = productData?.collections?.find(
        item => item.discountMessage !== null && item.discountMessage !== '',
      )

      let discountAmount = null
      let finalDiscountAmount = 0
      const rawDiscountMessage = messageObj?.discountMessage?.value || messageObj?.discountMessage
      const isPercentageDiscount = messageObj?.isPercent?.value || messageObj?.isPercent
      const discountValue = messageObj?.discountAmount?.value || messageObj?.discountAmount
      if (rawDiscountMessage !== null) {
        if (isPercentageDiscount && isPercentageDiscount === 'true') {
          discountAmount = (productData?.price?.amount * Number(discountValue)) / 100
        } else {
          discountAmount = Number(discountValue) * 100
        }
        finalDiscountAmount = this.$formatMoney(
          productData?.price?.amount - discountAmount,
          cnvs?.store.moneyFormat,
          true,
        )
        this.discountMessage = rawDiscountMessage?.replace('## amount ##', finalDiscountAmount)
      }
    },

    /**
     * Query a product using storefront API
     * @param {String} handle - Product handle
     */
    async queryProduct(handle) {
      if (!handle) {
        return false
      }

      try {
        const response = await queryFormatProduct(handle)
        return response
      } catch (error) {
        cnvs.ReportError('[Cart Item]', error)
        return false
      }
    },
  },
}
</script>

<style lang="scss">
@import './line-item';
</style>
